/** React Query hooks — the only place the UI talks to the API. */
import { keepPreviousData, useMutation, useQuery, useQueryClient, type Query, type QueryClient } from '@tanstack/react-query';
import type {
  AddSharedResult,
  AdminView,
  AudioMime,
  CoachMessage,
  DayTotals,
  DayView,
  Entry,
  EntryInput,
  FoodSearchResult,
  FoodView,
  HistoryFood,
  LoggedAmount,
  NewShareLink,
  OnboardInput,
  ParseResult,
  RecipeInput,
  RecipeShareView,
  RecipeSummary,
  RecipeView,
  RecurringFood,
  SessionView,
  SettingsView,
  SharedRecipePreview,
  UsualMeal,
  WeeklyRecap,
  WeightEntry,
  WeightInsight,
  WeightView,
  WhatFitsResult,
} from '../../shared/api';
import { aiResetRefetchDelay, nextAIReset } from '../../shared/aiAllowance';
import type { ISODate, Meal } from '../../shared/dates';
import { scaleMap, sumWithCoverage } from '../../shared/nutrients';
import type { ImportedRecipe, RecipeImportRequest } from '../../shared/recipeImport';
import { kitchenUnitsFor, type KitchenUnits } from '../../shared/units';
import { forgetAmountUnits } from '../lib/amountUnits';
import { toLastAmounts } from '../lib/amounts';
import { forgetAllLinks, keepAccountLinks } from '../lib/myShareLinks';
import { forgetSharedToken } from '../lib/openedShareLink';
import { api } from './client';

export const qk = {
  session: ['session'] as const,
  settings: ['settings'] as const,
  day: (date: ISODate) => ['day', date] as const,
  days: (from: ISODate, to: ISODate) => ['days', from, to] as const,
  weights: (range: string, today: ISODate) => ['weights', range, today] as const,
  history: (kind: 'recent' | 'frequent', meal: Meal | null) => ['history', kind, meal] as const,
  lastAmounts: ['lastAmounts'] as const,
  favorites: ['favorites'] as const,
  customFoods: ['customFoods'] as const,
  usual: (date: ISODate, meal: Meal) => ['usual', date, meal] as const,
  insight: (today: ISODate) => ['insight', today] as const,
  recap: (today: ISODate) => ['recap', today] as const,
  coach: ['coach'] as const,
  admin: ['admin'] as const,
  recurring: ['recurring'] as const,
  recipes: ['recipes'] as const,
  recipe: (id: string) => ['recipe', id] as const,
  sharedRecipe: (token: string) => ['sharedRecipe', token] as const,
};

// AI counts start over at midnight UTC. Reloads land up to 20 s later, fixed per tab,
// so open apps don't all ask in the same second.
const RESET_JITTER_MS = Math.floor(Math.random() * 20_000);

// ——— Sign-in

export function useSession() {
  return useQuery({ queryKey: qk.session, queryFn: () => api<SessionView>('/auth/session'), staleTime: 5 * 60_000, retry: 1 });
}

/** Admin tools exist only with sign-in on; locally there's just you. */
export function useIsAdmin(): boolean {
  const session = useSession();
  return Boolean(session.data?.required && session.data.user?.role === 'admin');
}

/**
 * After signing in or out: update the session in place (the gate is watching it),
 * then drop every other cached query, since they hold personal data.
 */
export function useApplySession() {
  const client = useQueryClient();
  return (session: SessionView) => {
    // Units remembered for foods belong to whoever chose them.
    forgetAmountUnits();
    client.setQueryData(qk.session, session);
    client.removeQueries({ predicate: (q) => q.queryKey[0] !== qk.session[0] });
    if (!session.signedIn) forgetShareLinksOnDevice();
    // Signed in: links another account made here aren't kept for this one.
    else if (session.user) keepAccountLinks(session.user.id);
  };
}

/** A session ended (signed out, expired, ended elsewhere): no share link, made or opened, stays on this device for the next person. */
export function forgetShareLinksOnDevice(): void {
  forgetAllLinks();
  forgetSharedToken();
}

export function useSignOut() {
  const apply = useApplySession();
  return useMutation({
    mutationFn: (opts: { everywhere?: boolean } = {}) => api<SessionView>(opts.everywhere ? '/auth/logout-all' : '/auth/logout', { method: 'POST', body: {} }),
    onSuccess: apply,
  });
}

// ——— Your account: sign-in emails and deleting the account (sign-in servers only)

/** Keep the session's list of sign-in addresses in step with the server's answer (and an admin's own row on Admin). */
function setSessionEmails(client: QueryClient, emails: string[]) {
  client.setQueryData<SessionView>(qk.session, (s) => (s?.user ? { ...s, user: { ...s.user, emails } } : s));
  void client.invalidateQueries({ queryKey: qk.admin });
}

/** Emails a 6-digit code to an address to add to this account. */
export function useStartLinkEmail() {
  return useMutation({
    mutationFn: (email: string) => api<{ ok: true }>('/account/emails', { method: 'POST', body: { email } }),
  });
}

export function useVerifyLinkEmail() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; code: string }) => api<{ emails: string[] }>('/account/emails/verify', { method: 'POST', body: input }),
    onSuccess: ({ emails }) => setSessionEmails(client, emails),
  });
}

export function useUnlinkEmail() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api<{ emails: string[] }>(`/account/emails/${encodeURIComponent(email)}`, { method: 'DELETE' }),
    onSuccess: ({ emails }, email) => {
      setSessionEmails(client, emails);
      // Removing the address this browser signed in with ends its session: re-check to show sign-in.
      if (client.getQueryData<SessionView>(qk.session)?.email === email) void client.invalidateQueries({ queryKey: qk.session });
    },
  });
}

/** Deletes the account and all its data; the answer is the signed-out session. */
export function useDeleteAccount() {
  const apply = useApplySession();
  return useMutation({
    mutationFn: () => api<SessionView>('/account/delete', { method: 'POST', body: { confirm: 'delete' } }),
    onSuccess: apply,
  });
}

// ——— Admin: invites, members and AI allowances (admins only)

export function useAdmin() {
  return useQuery({
    queryKey: qk.admin,
    queryFn: () => api<AdminView>('/admin'),
    staleTime: 30_000,
    // Everyone's AI total starts over at midnight UTC too.
    refetchInterval: () => aiResetRefetchDelay(nextAIReset().toISOString(), Date.now(), RESET_JITTER_MS),
  });
}

/** Every admin write answers with the whole admin view. A failure may mean the list is stale (an invite already used, someone already removed): reload it. */
function useAdminMutation<TInput>(request: (input: TInput) => Promise<AdminView>, after?: (client: QueryClient, input: TInput) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (view, input) => {
      client.setQueryData(qk.admin, view);
      after?.(client, input);
    },
    onError: () => void client.invalidateQueries({ queryKey: qk.admin }),
  });
}

export const useInvite = () => useAdminMutation((email: string) => api<AdminView>('/admin/invites', { method: 'POST', body: { email } }));
export const useRevokeInvite = () =>
  useAdminMutation((email: string) => api<AdminView>(`/admin/invites/${encodeURIComponent(email)}`, { method: 'DELETE' }));
export const useSetAiLimit = () =>
  useAdminMutation(
    ({ id, aiDailyLimit }: { id: string; aiDailyLimit: number | null }) =>
      api<AdminView>(`/admin/members/${encodeURIComponent(id)}`, { method: 'PATCH', body: { aiDailyLimit } }),
    // Your own allowance also shows on You.
    (client, { id }) => {
      if (client.getQueryData<SessionView>(qk.session)?.user?.id === id) void client.invalidateQueries({ queryKey: qk.settings });
    },
  );
export const useRemoveMember = () =>
  useAdminMutation((id: string) => api<AdminView>(`/admin/members/${encodeURIComponent(id)}`, { method: 'DELETE' }));

// ——— Queries

/** Reload settings when today's AI allowance starts over (midnight UTC), so a "come back at" state clears by itself. */
function untilAIReset(query: Query<SettingsView>): number | false {
  return aiResetRefetchDelay(query.state.data?.aiUsage.resetsAt, Date.now(), RESET_JITTER_MS);
}

/** `fresh` refetches when the caller mounts: You shows today's AI usage, which every AI call changes. */
export function useSettings(opts: { fresh?: boolean } = {}) {
  return useQuery({
    queryKey: qk.settings,
    queryFn: () => api<SettingsView>('/settings'),
    staleTime: 60_000,
    refetchOnMount: opts.fresh ? 'always' : true,
    refetchInterval: untilAIReset,
  });
}

const selectKitchenUnits = (settings: SettingsView) => kitchenUnitsFor(settings.profile);

/**
 * Cups and spoons, or metric, for something worked out once (an import's draft): from the settings
 * the app has loaded, fetched first if they haven't arrived yet. Cups and spoons if they can't be had.
 */
export async function kitchenUnitsNow(client: QueryClient): Promise<KitchenUnits> {
  try {
    return selectKitchenUnits(await client.ensureQueryData({ queryKey: qk.settings, queryFn: () => api<SettingsView>('/settings') }));
  } catch {
    return 'us';
  }
}

/**
 * Cups and spoons, or metric, for every amount control. It reads the settings query the app has
 * already loaded, so a page full of amounts doesn't ask for settings again each time one opens.
 */
export function useKitchenUnits(): KitchenUnits {
  const { data } = useQuery({
    queryKey: qk.settings,
    queryFn: () => api<SettingsView>('/settings'),
    staleTime: 60_000,
    refetchOnMount: false,
    select: selectKitchenUnits,
  });
  return data ?? 'us';
}

export function useDay(date: ISODate, today: ISODate) {
  return useQuery({
    queryKey: qk.day(date),
    queryFn: () => api<DayView>(`/days/${date}?today=${today}`),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function prefetchDay(client: QueryClient, date: ISODate, today: ISODate) {
  return client.prefetchQuery({ queryKey: qk.day(date), queryFn: () => api<DayView>(`/days/${date}?today=${today}`), staleTime: 15_000 });
}

export function useDays(from: ISODate, to: ISODate) {
  return useQuery({
    queryKey: qk.days(from, to),
    queryFn: () => api<DayTotals[]>(`/days?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useWeights(range: string, today: ISODate) {
  return useQuery({
    queryKey: qk.weights(range, today),
    queryFn: () => api<WeightView>(`/weights?range=${range}&today=${today}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useHistory(kind: 'recent' | 'frequent', meal: Meal | null, enabled = true) {
  return useQuery({
    queryKey: qk.history(kind, meal),
    queryFn: () => api<HistoryFood[]>(`/foods/history?kind=${kind}${meal ? `&meal=${meal}` : ''}`),
    enabled,
    staleTime: 30_000,
  });
}

/** The amount last logged of each food, by ref, and how recently, so search opens a food at what you had last time. */
export function useLastAmounts() {
  return useQuery({
    queryKey: qk.lastAmounts,
    // Most recently logged first. A list, not an object, so a change of order alone is a change of data.
    queryFn: () => api<LoggedAmount[]>('/foods/last-amounts'),
    select: toLastAmounts,
    staleTime: 60_000,
  });
}

export function useFavorites(enabled = true) {
  return useQuery({ queryKey: qk.favorites, queryFn: () => api<HistoryFood[]>('/foods/favorites'), enabled, staleTime: 30_000 });
}

export function useCustomFoods() {
  return useQuery({ queryKey: qk.customFoods, queryFn: () => api<FoodView[]>('/foods/custom'), staleTime: 60_000 });
}

/** Server-side search (FatSecret when configured). Callers debounce the query. */
export function useFoodDbSearch(query: string, enabled: boolean) {
  const q = query.trim();
  return useQuery({
    queryKey: ['foodSearch', q.toLowerCase()],
    queryFn: ({ signal }) => api<FoodSearchResult>(`/foods/search?q=${encodeURIComponent(q)}`, { signal }),
    enabled: enabled && q.length >= 2,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Full details (servings, per-100 g nutrients) for any food ref; cached by React Query. */
export function fetchFoodItem(client: QueryClient, ref: string) {
  return client.fetchQuery({
    queryKey: ['foodItem', ref],
    queryFn: () => api<FoodView>(`/foods/item/${encodeURIComponent(ref)}`),
    staleTime: 30 * 60_000,
  });
}

export function useUsual(date: ISODate, meal: Meal) {
  return useQuery({
    queryKey: qk.usual(date, meal),
    queryFn: () => api<UsualMeal[]>(`/suggestions/usual?date=${date}&meal=${meal}`),
    staleTime: 60_000,
  });
}

// The insight and recap load on their own. When the server has to write a new one, that's a billable
// call: refresh the allowance afterwards (a cached answer costs nothing, and neither does checking).
export function useWeightInsight(today: ISODate, enabled: boolean) {
  const client = useQueryClient();
  return useQuery({
    queryKey: qk.insight(today),
    queryFn: () => api<{ insight: WeightInsight | null }>(`/ai/weight-insight?today=${today}`).finally(() => refreshAIUsage(client)),
    enabled,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useWeeklyRecap(today: ISODate, enabled: boolean) {
  const client = useQueryClient();
  return useQuery({
    queryKey: qk.recap(today),
    queryFn: () => api<{ recap: WeeklyRecap | null }>(`/ai/weekly-recap?today=${today}`).finally(() => refreshAIUsage(client)),
    enabled,
    staleTime: 60 * 60_000,
    retry: false,
  });
}

export function useCoachMessages() {
  return useQuery({ queryKey: qk.coach, queryFn: () => api<CoachMessage[]>('/coach/messages'), staleTime: Infinity });
}

// ——— Entry mutations (optimistic where it makes the UI feel instant)

function recomputeDay(day: DayView, entries: Entry[]): DayView {
  const scaled = entries.map((e) => scaleMap(e.per100, e.grams));
  const { totals, known } = sumWithCoverage(scaled);
  return {
    ...day,
    totals,
    known,
    entryCount: entries.length,
    meals: day.meals.map((m) => {
      const list = entries.filter((e) => e.meal === m.meal);
      return { ...m, entries: list, totals: sumWithCoverage(list.map((e) => scaleMap(e.per100, e.grams))).totals };
    }),
  };
}

/** Refetch everything food-related; resolves once the visible queries have reloaded. */
function refetchFood(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['day'] }),
    client.invalidateQueries({ queryKey: ['days'] }),
    client.invalidateQueries({ queryKey: ['history'] }),
    client.invalidateQueries({ queryKey: qk.lastAmounts }),
    client.invalidateQueries({ queryKey: ['usual'] }),
    client.invalidateQueries({ queryKey: qk.favorites }),
  ]);
}

function invalidateFood(client: QueryClient) {
  void refetchFood(client);
}

export function useAddEntries() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entries: EntryInput[]) => api<Entry[]>('/entries', { method: 'POST', body: { entries } }),
    onSuccess: (created) => {
      for (const date of new Set(created.map((e) => e.date))) {
        client.setQueryData<DayView>(qk.day(date), (day) =>
          day ? recomputeDay(day, [...day.meals.flatMap((m) => m.entries), ...created.filter((e) => e.date === date)]) : day,
        );
      }
      invalidateFood(client);
    },
  });
}

export function useUpdateEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; date: ISODate; patch: { grams?: number; meal?: Meal; date?: ISODate } }) =>
      api<Entry>(`/entries/${id}`, { method: 'PATCH', body: patch }),
    onMutate: async ({ id, date, patch }) => {
      await client.cancelQueries({ queryKey: qk.day(date) });
      const previous = client.getQueryData<DayView>(qk.day(date));
      if (previous) {
        const entries = previous.meals
          .flatMap((m) => m.entries)
          .map((e) => (e.id === id ? { ...e, ...patch } : e))
          .filter((e) => e.date === date);
        client.setQueryData(qk.day(date), recomputeDay(previous, entries));
      }
      return { previous, date };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) client.setQueryData(qk.day(ctx.date), ctx.previous);
    },
    onSettled: () => invalidateFood(client),
  });
}

export function useDeleteEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; date: ISODate }) => api<void>(`/entries/${id}`, { method: 'DELETE' }),
    onMutate: async ({ id, date }) => {
      await client.cancelQueries({ queryKey: qk.day(date) });
      const previous = client.getQueryData<DayView>(qk.day(date));
      if (previous) {
        client.setQueryData(
          qk.day(date),
          recomputeDay(
            previous,
            previous.meals.flatMap((m) => m.entries).filter((e) => e.id !== id),
          ),
        );
      }
      return { previous, date };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) client.setQueryData(qk.day(ctx.date), ctx.previous);
    },
    onSettled: () => invalidateFood(client),
  });
}

export function useCopyEntries() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { fromDate: ISODate; toDate: ISODate; meal?: Meal }) =>
      api<Entry[]>('/entries/copy', { method: 'POST', body: input }),
    // Stay pending until the day reloads, so "Copy yesterday" can't run twice.
    onSuccess: () => refetchFood(client),
  });
}

// ——— Daily foods (the server adds a copy to the log every day)

export function useRecurring() {
  return useQuery({ queryKey: qk.recurring, queryFn: () => api<RecurringFood[]>('/recurring'), staleTime: 30_000 });
}

/**
 * Starting, changing or stopping a daily food changes the log too: entries gain
 * or lose their link (the "Daily" mark), and today's copy may have just been added.
 */
function refetchRecurring(client: QueryClient) {
  return Promise.all([client.invalidateQueries({ queryKey: qk.recurring }), refetchFood(client)]);
}

/** Makes a logged entry's food a daily food. */
export function useStartRecurring() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => api<RecurringFood>('/recurring', { method: 'POST', body: { entryId } }),
    onSuccess: () => void refetchRecurring(client),
    // Say it already repeats (started on another device): reload so this screen catches up.
    onError: () => void refetchRecurring(client),
  });
}

/** Changes the amount or meal for days not added yet. */
export function useUpdateRecurring() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { grams?: number; meal?: Meal } }) =>
      api<RecurringFood>(`/recurring/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
    onSuccess: (food) => {
      client.setQueryData<RecurringFood[]>(qk.recurring, (list) => list?.map((r) => (r.id === food.id ? food : r)));
      void refetchRecurring(client);
    },
    // Say it was stopped on another device: reload so this screen catches up.
    onError: () => void refetchRecurring(client),
  });
}

/** Stops a daily food: its copies stay, no new ones are added. */
export function useStopRecurring() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/recurring/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: (_done, id) => {
      client.setQueryData<RecurringFood[]>(qk.recurring, (list) => list?.filter((r) => r.id !== id));
      void refetchRecurring(client);
    },
    // Already stopped somewhere else (404): reload so this screen catches up.
    onError: () => void refetchRecurring(client),
  });
}

// ——— Foods

export function useCreateCustomFood() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      brand?: string | null;
      servingLabel: string;
      servingGrams: number;
      perServing: Record<string, number>;
      liquid?: boolean;
    }) => api<FoodView>('/foods/custom', { method: 'POST', body: input }),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.customFoods }),
  });
}

export function useToggleFavorite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ ref, entryId, on }: { ref?: string; entryId?: string; on: boolean }) => {
      if (on) return api<{ ref: string }>('/foods/favorites', { method: 'POST', body: { ref, entryId } });
      if (!ref) return null;
      await api<void>(`/foods/favorites/${encodeURIComponent(ref)}`, { method: 'DELETE' });
      return null;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.favorites });
      void client.invalidateQueries({ queryKey: ['history'] });
      void client.invalidateQueries({ queryKey: qk.customFoods });
      void client.invalidateQueries({ queryKey: ['day'] });
      // Starring a deleted recipe brings it back.
      void client.invalidateQueries({ queryKey: qk.recipes });
    },
  });
}

// ——— Recipes (each one is also a food, "recipe:<id>", logged with useAddEntries)

const fetchRecipes = () => api<RecipeSummary[]>('/recipes');

export function useRecipes() {
  return useQuery({ queryKey: qk.recipes, queryFn: fetchRecipes, staleTime: 60_000 });
}

const recipeFoods = (list: RecipeSummary[]) => list.map((r) => r.food);

/** Your recipes as foods, for search. Shares the recipe list's query. */
export function useRecipeFoods(enabled = true) {
  return useQuery({ queryKey: qk.recipes, queryFn: fetchRecipes, staleTime: 60_000, select: recipeFoods, enabled });
}

export function useRecipe(id: string) {
  return useQuery({ queryKey: qk.recipe(id), queryFn: () => api<RecipeView>(`/recipes/${encodeURIComponent(id)}`), staleTime: 30_000 });
}

/** Recent, Favorites and usual meals show a recipe as it is now: reload them after a change. */
function afterRecipeChange(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: qk.recipes }),
    client.invalidateQueries({ queryKey: ['history'] }),
    client.invalidateQueries({ queryKey: ['usual'] }),
    client.invalidateQueries({ queryKey: qk.favorites }),
  ]);
}

/** Creates a recipe, or replaces one (a new version) when there's an id. */
export function useSaveRecipe() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: RecipeInput }) =>
      id
        ? api<RecipeView>(`/recipes/${encodeURIComponent(id)}`, { method: 'PUT', body: input })
        : api<RecipeView>('/recipes', { method: 'POST', body: input }),
    onSuccess: (recipe) => {
      client.setQueryData(qk.recipe(recipe.id), recipe);
      void afterRecipeChange(client);
    },
    // Say it was deleted on another device: reload so this screen catches up.
    onError: (_err, { id }) => {
      if (id) void client.invalidateQueries({ queryKey: qk.recipe(id) });
    },
  });
}

/** Deletes a recipe. What's already logged stays, and Recent can still log it. */
export function useDeleteRecipe() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/recipes/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: (_done, id) => {
      client.setQueryData<RecipeSummary[]>(qk.recipes, (list) => list?.filter((r) => r.id !== id));
      // Not reloaded now (the page that deleted it is leaving); opening it again finds it gone.
      void client.invalidateQueries({ queryKey: qk.recipe(id), refetchType: 'none' });
      void afterRecipeChange(client);
    },
  });
}

/**
 * Reads a recipe from a link, pasted text or a photo into a draft for the editor.
 * Nothing is saved. It can use one of today's AI requests, so the count reloads.
 */
export function useImportRecipe() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ request, signal }: { request: RecipeImportRequest; signal?: AbortSignal }) =>
      api<ImportedRecipe>('/recipes/import', { method: 'POST', body: request, signal }),
    // Offline, fail right away ("Can't reach the server") instead of waiting, paused, behind a progress screen.
    networkMode: 'always',
    onSettled: () => refreshAIUsage(client),
  });
}

// ——— Sharing recipes by link. A link's token only ever goes in a POST body, never a URL.

/** The recipe page and the list follow the link's state: on or off, whether it shows your name, the count. */
function setSharing(client: QueryClient, id: string, sharing: RecipeShareView) {
  client.setQueryData<RecipeView>(qk.recipe(id), (r) => (r ? { ...r, sharing, shared: sharing.link !== null } : r));
  void client.invalidateQueries({ queryKey: qk.recipes });
}

/** Makes a link for one of your recipes, replacing the one that's on. Its address comes back this once. */
export function useCreateShareLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, showName }: { id: string; showName: boolean }) =>
      api<NewShareLink>(`/recipes/${encodeURIComponent(id)}/share`, { method: 'POST', body: { showName } }),
    onSuccess: ({ sharing }, { id }) => setSharing(client, id, sharing),
  });
}

/** Shows or hides your name on the recipe's link, right away. */
export function useShareShowName() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, showName }: { id: string; showName: boolean }) =>
      api<RecipeShareView>(`/recipes/${encodeURIComponent(id)}/share`, { method: 'PATCH', body: { showName } }),
    onSuccess: (sharing, { id }) => setSharing(client, id, sharing),
  });
}

/** Turn off link: it stops working at once, for opening, adding and refreshing. */
export function useTurnOffShareLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<RecipeShareView>(`/recipes/${encodeURIComponent(id)}/share`, { method: 'DELETE' }),
    onSuccess: (sharing, id) => setSharing(client, id, sharing),
  });
}

/** Refresh: your copy takes the original's latest version. What you logged keeps its own numbers. */
export function useRefreshRecipe() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<RecipeView>(`/recipes/${encodeURIComponent(id)}/refresh`, { method: 'POST', body: {} }),
    onSuccess: (recipe) => {
      client.setQueryData(qk.recipe(recipe.id), recipe);
      void afterRecipeChange(client);
    },
    // Say the link was turned off meanwhile: reload, so the page stops offering it.
    onError: (_err, id) => void client.invalidateQueries({ queryKey: qk.recipe(id) }),
  });
}

/** A shared recipe, from its link's token. */
export function useSharedRecipe(token: string | null) {
  return useQuery({
    queryKey: qk.sharedRecipe(token ?? ''),
    queryFn: () => api<SharedRecipePreview>('/shared/recipes/preview', { method: 'POST', body: { token } }),
    enabled: token !== null,
    staleTime: 30_000,
  });
}

/** Add to my recipes, or Update my copy (a copy from an older link). */
export function useAddSharedRecipe() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api<AddSharedResult>('/shared/recipes/add', { method: 'POST', body: { token } }),
    // The preview query is left to expire: removing it while the page still watches it would fetch it again.
    onSuccess: ({ recipe }) => {
      client.setQueryData(qk.recipe(recipe.id), recipe);
      void afterRecipeChange(client);
    },
    // Turned off meanwhile, say: the preview catches up.
    onError: (_err, token) => void client.invalidateQueries({ queryKey: qk.sharedRecipe(token) }),
  });
}

// ——— Weight

function invalidateWeight(client: QueryClient) {
  void client.invalidateQueries({ queryKey: ['weights'] });
  void client.invalidateQueries({ queryKey: qk.settings });
  void client.invalidateQueries({ queryKey: ['day'] });
  void client.invalidateQueries({ queryKey: ['insight'] });
  // The weekly check-in counts from the stretch in effect that week, so a changed start can change it.
  void client.invalidateQueries({ queryKey: ['recap'] });
}

export function useSaveWeight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id?: string; date: ISODate; kg: number; note?: string | null; startingWeight?: boolean }) =>
      id
        ? api<WeightEntry>(`/weights/${id}`, { method: 'PATCH', body: input })
        : api<WeightEntry>('/weights', { method: 'POST', body: input }),
    onSuccess: () => invalidateWeight(client),
  });
}

export function useDeleteWeight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/weights/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateWeight(client),
  });
}

// ——— Settings

function useSettingsMutation<TInput>(path: string, method: 'PATCH' | 'PUT' | 'POST' | 'DELETE', toPath?: (input: TInput) => string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      api<SettingsView>(toPath ? toPath(input) : path, { method, body: method === 'DELETE' ? undefined : input }),
    onSuccess: (settings) => {
      client.setQueryData(qk.settings, settings);
      void client.invalidateQueries({ queryKey: ['day'] });
      void client.invalidateQueries({ queryKey: ['weights'] });
      void client.invalidateQueries({ queryKey: ['insight'] });
      void client.invalidateQueries({ queryKey: ['recap'] });
    },
  });
}

export const useUpdateProfile = () => useSettingsMutation<Record<string, unknown>>('/settings/profile', 'PATCH');
export const useUpdateGoals = () =>
  useSettingsMutation<{ mode: 'suggested' | 'custom'; kcal?: number; protein?: number; carbs?: number; fat?: number }>('/settings/goals', 'PUT');
export const useUpdateTracking = () =>
  useSettingsMutation<{ items: Array<{ key: string; tracked: boolean; target: number | null; direction: 'min' | 'max' | 'target' }> }>(
    '/settings/tracking',
    'PUT',
  );
export const useCreateNutrient = () =>
  useSettingsMutation<{ name: string; unit: string; direction: 'min' | 'max' | 'target'; target: number | null }>('/settings/nutrients', 'POST');
export const useDeleteNutrient = () =>
  useSettingsMutation<string>('', 'DELETE', (key) => `/settings/nutrients/${encodeURIComponent(key)}`);

export function useOnboard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: OnboardInput) => api<SettingsView>('/account/onboard', { method: 'POST', body: input }),
    onSuccess: (settings) => {
      client.setQueryData(qk.settings, settings);
      void client.invalidateQueries();
    },
  });
}

export function useResetAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<SettingsView>('/account/reset', { method: 'POST', body: { confirm: 'erase' } }),
    onSuccess: (settings) => {
      // Drop the erased data but keep the session and settings queries: removing a query cuts off
      // the components already watching it (AuthGate, App), so later sign-out and setup wouldn't show.
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== qk.session[0] && q.queryKey[0] !== qk.settings[0] });
      client.setQueryData(qk.settings, settings);
    },
  });
}

export function useResetDemo() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (today: ISODate) => api<{ weights: number; entries: number }>('/demo/reset', { method: 'POST', body: { today } }),
    onSuccess: () => client.invalidateQueries(),
  });
}

// ——— AI

/**
 * Every AI call counts toward today's allowance (settings.aiUsage), which You and the
 * AI actions show. An admin's page also shows everyone's total; it reloads only if open.
 */
export function refreshAIUsage(client: QueryClient) {
  void client.invalidateQueries({ queryKey: qk.settings });
  void client.invalidateQueries({ queryKey: qk.admin });
}

/**
 * A voice clip or barcode photo goes to the AI once: it fails right away offline instead
 * of pausing (and being charged whenever the connection returns), and it isn't kept in
 * the mutation cache afterwards. Callers reset() once they have the answer.
 */
const MEDIA_ONCE = { networkMode: 'always', gcTime: 0 } as const;

/** Speech to text on the server, for browsers without working speech recognition (Brave, Firefox). */
export function useTranscribe() {
  const client = useQueryClient();
  return useMutation({
    ...MEDIA_ONCE,
    mutationFn: (input: { audio: { mimeType: AudioMime; base64: string }; language?: string }) =>
      api<{ transcript: string }>('/ai/transcribe', { method: 'POST', body: input }),
    onSettled: () => refreshAIUsage(client),
  });
}

/** Barcode digits read by the AI from a photo the browser couldn't decode. */
export function useReadBarcodePhoto() {
  const client = useQueryClient();
  return useMutation({
    ...MEDIA_ONCE,
    mutationFn: (image: { mimeType: 'image/jpeg'; base64: string }) => api<{ code: string | null }>('/ai/barcode', { method: 'POST', body: { image } }),
    onSettled: () => refreshAIUsage(client),
  });
}

export function useParseFood() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      date: ISODate;
      meal?: Meal;
      mode: 'meal' | 'label';
      text?: string;
      image?: { mimeType: 'image/jpeg'; base64: string };
      hour: number;
    }) => api<ParseResult>('/ai/parse', { method: 'POST', body: input }),
    onSettled: () => refreshAIUsage(client),
  });
}

export function useWhatFits() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      date: ISODate;
      meal?: Meal;
      mode: 'mine' | 'new' | 'menu';
      menuText?: string;
      menuImage?: { mimeType: 'image/jpeg'; base64: string };
      hour: number;
    }) => api<WhatFitsResult>('/ai/what-fits', { method: 'POST', body: input }),
    onSettled: () => refreshAIUsage(client),
  });
}

export function useRefreshInsight() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (today: ISODate) => api<{ insight: WeightInsight | null }>('/ai/weight-insight/refresh', { method: 'POST', body: { today } }),
    onSuccess: (data, today) => client.setQueryData(qk.insight(today), data),
    onSettled: () => refreshAIUsage(client),
  });
}

export function useRefreshRecap() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (today: ISODate) => api<{ recap: WeeklyRecap | null }>('/ai/weekly-recap/refresh', { method: 'POST', body: { today } }),
    onSuccess: (data, today) => client.setQueryData(qk.recap(today), data),
    onSettled: () => refreshAIUsage(client),
  });
}

export function useClearCoach() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/coach/messages', { method: 'DELETE' }),
    onSuccess: () => client.setQueryData(qk.coach, []),
  });
}
